import { useEffect, useCallback, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import { useVerificationStore, flushPendingFieldWrites, isOperatorKeyable, selectBatchLabel } from "../state/store";
import { stageForStep } from "@capture/shared";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DocumentCanvas } from "./DocumentCanvas";
import { FieldPanel } from "./FieldPanel";
import { LineItemGrid, type LineItemColumn } from "./LineItemGrid";
import { RowCheckSummary } from "./RowCheckSummary";
import { KeyboardManager } from "./KeyboardManager";
import { StatusBar } from "./StatusBar";
import { SnippetPanel } from "./SnippetPanel";
import { TerminalModeView } from "./TerminalModeView";
import { KeyboardShortcutModal } from "./KeyboardShortcutModal";
import { MisidentifiedTypeDialog } from "./MisidentifiedTypeDialog";
import { escalateFlagOutcome } from "../escalate-flag-outcome";
import { AuditTrailPanel } from "./AuditTrailPanel";
import { BatchTreeDrawer } from "./BatchTreeDrawer";
import { RevalidationOverlay } from "./RevalidationOverlay";
import { BboxLegend, BboxLegendHeaderButton } from "./BboxLegend";
import { BboxOverlayHeaderButton } from "./BboxOverlayToolbar";
import { VerificationCheckInButton } from "./VerificationCheckInButton";
import { useFeedLock } from "@/features/queues/hooks/useFeedLock";
import { useBatchTransitionEffect } from "../lib/cross-batch";
import { CrossBatchConfirmModal } from "./CrossBatchConfirmModal";
import { QueueBlockedModal } from "./QueueBlockedModal";
import { VerificationKeystrokeRecorder } from "./VerificationKeystrokeRecorder";
import { QueueInstructionStrip } from "./QueueInstructionStrip";
import { DirectedNoteCard } from "@/features/directed-notes/components/DirectedNoteCard";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { api, ApiError } from "@/lib/api-client";
import { toast } from "@/components/Toaster";
import {
  formatEvictionNotice,
  type QueueEvictionEvent,
} from "@/features/queue-evictions/api/client";
import type { GetNextResponse } from "@/features/queues/lib/queue-claim";
import { useContentValidateBundle, pickActiveEntry } from "../hooks/useContentValidateBundle";
import { ContentValidateContext } from "../context/ContentValidateContext";
import { LookupSnapshotContext, JobConstantsContext } from "../context/LookupSnapshotContext";
import { OverrideDialog } from "./OverrideDialog";
import {
  resolveOverrideAction,
  persistFieldOverride,
  resolveOverrideRuleMessage,
  type OverrideAuditContext,
} from "../lib/override-policy";
import { mapApiToPayload, type ApiDocument } from "../lib/map-api-to-payload";
import { Toaster } from "@/components/Toaster";
import { usePrefetch } from "../prefetch/usePrefetch";
import { useApplyPreferences } from "../hooks/useApplyPreferences";
import { useViewerFitModePersist } from "../hooks/useViewerFitModePersist";
import { useDetachablePanel } from "../hooks/useDetachablePanel";
import { useReleaseOnExit } from "../hooks/useReleaseOnExit";
import { useCurrentJobType } from "../hooks/useCurrentJobType";
import {
  computeSmartSkipVisitSet,
  jobTypeThresholdAsTypeFloors,
  type SmartSkipField,
  type StepReverifyPolicy,
} from "../hooks/useSmartSkipIterator";
import { useWsClient } from "@/lib/ws-provider";
import { formatStep } from "@/lib/formatters";
import { FieldRuleReadout } from "./FieldRuleReadout.js";
import { SessionTimer } from "./SessionTimer";
import type { WebhookCheckStatus, WebhookCheckOnUnresolved, FieldState } from "../state/types";
import "./VerificationStation.css";

/**
 * R-89 — build an operator-actionable message for a failed page-accept.
 * The accept POST previously had no `.catch`, so a genuine rejection was
 * swallowed (no toast, no retry, optimistic UI left half-done). Carry the
 * server reason for 4xx; always stay actionable; include the status code
 * for a bodyless 4xx so the operator can report it.
 */
export function acceptErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    const reason = body?.error ?? body?.message;
    if (reason) return `Couldn't accept this document: ${reason}`;
    return `Couldn't accept this document (error ${err.status}) — please retry.`;
  }
  return "Couldn't accept this document — please retry.";
}

/**
 * S-5 Task 7 — the (unwrapped) `webhook_check_update` WS payload, as
 * published by `executeWebhookCheck` (S-5 Task 4/5,
 * apps/api/src/services/rules/webhook-check-dispatch.ts).
 *
 * GAP (flagged, not fixed here — apps/api is a shared checkout other S-5
 * lanes are actively editing, and neither file is in this task's file
 * list): the LIVE producer publishes `{documentId, ruleId, ruleName,
 * status, message?, checkedAt, requestHash}` — `fieldRef` and
 * `onUnresolved` are NOT on the wire today. Both are modeled here as
 * optional, forward-compat fields so `useWebhookCheckUpdates` below reads
 * them the moment the producer is extended (a one-line
 * `fieldRef: binding.fieldRef, onUnresolved: config.onUnresolved` addition
 * to that `publishToChannel` call) without any client-side change. Until
 * then: the per-field pending chip (FieldInput.tsx) simply has nothing to
 * attach to (no fieldRef to match), but the boundary-spinner auto-retry and
 * the onUnresolved routing below — which don't need field identity — work
 * correctly against today's real payload (see
 * VerificationStation-webhook-check.test.tsx's "today's real wire shape"
 * case). See task-7-report.md.
 */
export interface WebhookCheckWireMessage {
  documentId: string;
  fieldRef?: string;
  ruleId: string;
  ruleName?: string | null;
  status: WebhookCheckStatus;
  message?: string | null;
  onUnresolved?: WebhookCheckOnUnresolved;
}

/**
 * S-5 Task 7 — subscribes to `webhook_check_update` WS events (published on
 * `queue:<tenantId>`, same channel/envelope as `document_moved` —
 * `useDocumentMovedBump` above is the precedent this mirrors) and, for an
 * event matching the currently-open document:
 *   1. forwards it to the store's `applyWebhookCheckUpdate` (drives the
 *      FieldInput pending/failed/unresolved chip) whenever a `fieldRef` is
 *      present on the payload (see the GAP note on `WebhookCheckWireMessage`);
 *   2. calls `onUnresolved` for an `'unresolved'` status (routes to the
 *      override/escalate dialog or the hold banner — design §5);
 *   3. calls `onResolved` for `'passed'`/`'failed'` (the boundary-spinner
 *      auto-retry signal — a 'pending' status fires NEITHER, it's still
 *      waiting).
 *
 * Exported for unit testing only (same pattern as `useDocumentMovedBump`).
 */
export function useWebhookCheckUpdates(opts: {
  currentDocumentId: string | undefined;
  onResolved?: (msg: WebhookCheckWireMessage) => void;
  onUnresolved?: (msg: WebhookCheckWireMessage) => void;
}): void {
  const { currentDocumentId, onResolved, onUnresolved } = opts;
  const wsClient = useWsClient();

  useEffect(() => {
    if (!wsClient || !currentDocumentId) return;

    const off = wsClient.onPayload("webhook_check_update", (raw) => {
      const msg = raw as WebhookCheckWireMessage;
      if (msg.documentId !== currentDocumentId) return;

      if (msg.fieldRef) {
        useVerificationStore.getState().applyWebhookCheckUpdate({
          fieldRef: msg.fieldRef,
          ruleId: msg.ruleId,
          ruleName: msg.ruleName,
          status: msg.status,
          message: msg.message,
          onUnresolved: msg.onUnresolved,
        });
      }

      if (msg.status === "unresolved") {
        onUnresolved?.(msg);
      } else if (msg.status !== "pending") {
        onResolved?.(msg);
      }
    });

    return off;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsClient, currentDocumentId]);
}

/**
 * S-5 Task 7 — distinguishes the accept POST's two 409 shapes (design §4,
 * Task 6): `{error: "checks_pending", pendingChecks}` is "wait and retry"
 * (show the boundary spinner instead of the error toast); everything else —
 * including the existing `{error: "cannot_advance", reason}` — falls
 * through to the unchanged `acceptErrorMessage`/toast+Retry path (`kind:
 * "other"`). A malformed/missing `pendingChecks` also falls through to
 * "other" rather than crash on a bad shape.
 */
export function classifyAcceptError(
  err: unknown,
): { kind: "checks_pending"; pendingChecks: number } | { kind: "other" } {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; pendingChecks?: number } | null;
    if (body?.error === "checks_pending" && typeof body.pendingChecks === "number") {
      return { kind: "checks_pending", pendingChecks: body.pendingChecks };
    }
  }
  return { kind: "other" };
}

/**
 * S-5 Task 7 — maps a webhook rule's `onUnresolved` config (design §2/§5) to
 * the station affordance it drives: `'escalate'` → the escalate flow,
 * `'hold'` → the batch-held banner, `'soft'` (and `undefined`) → the
 * override dialog. `undefined` deliberately resolves the SAME as `'soft'` —
 * that IS the server's own default (`WebhookValidateConfig.onUnresolved`,
 * packages/shared/src/types/rules.ts) — so the station behaves correctly
 * today even though the live WS payload doesn't yet carry `onUnresolved`
 * (see `WebhookCheckWireMessage`'s GAP note): every unresolved check reads
 * as the safe default until the producer is extended.
 */
export function resolveWebhookUnresolvedAction(
  onUnresolved: WebhookCheckOnUnresolved | undefined,
): "override" | "escalate" | "hold" {
  if (onUnresolved === "escalate") return "escalate";
  if (onUnresolved === "hold") return "hold";
  return "override";
}

/**
 * S-5 Task 7 review round 2, item 1 — the REAL persistence step for a
 * confirmed webhook-check override (design §2 "reason + audit"). Reuses the
 * EXACT SAME endpoint + audit model the field-rule Ctrl+O override already
 * uses (`persistFieldOverride`, `POST /documents/:id/override` ->
 * `DocumentField.isOverridden`/`overrideReason` + an `auditEntry` row —
 * apps/api/src/routes/documents/index.ts's `"/:id/override"` handler),
 * tagged `ruleType:"webhook_check"` (an existing, already-optional metadata
 * field on that endpoint) so the audit trail distinguishes it from a
 * field-rule override without inventing a parallel persistence path.
 * Exported so "real write, not a stub" is unit-testable without mounting
 * the full station (mirrors classifyAcceptError / resolveWebhookUnresolvedAction).
 */
export async function persistWebhookOverride(
  documentId: string,
  fieldId: string,
  reason: string,
  ruleName: string,
  ruleMessage?: string,
): Promise<{ ok: boolean; forbidden: boolean }> {
  return persistFieldOverride(documentId, fieldId, reason, undefined, {
    fieldLabel: ruleName,
    ruleType: "webhook_check",
    ...(ruleMessage !== undefined ? { ruleMessage } : {}),
  });
}

/**
 * S-5 Task 7 review round 2, item 2 — which webhook-rule ids the
 * boundary-spinner's "Retry check" button should refire: every field
 * currently showing a 'pending' or 'unresolved' chip that carries a
 * `webhookCheckRuleId`, deduped (more than one field can share a rule in an
 * unusual authoring setup). 'failed'/'passed'/absent are excluded — a
 * failed check answered "no" (re-dialing won't change that; the operator's
 * move is override/escalate) and there's nothing to retry for a clean field.
 */
export function collectPendingWebhookRuleIds(fields: readonly FieldState[]): string[] {
  const ids = new Set<string>();
  for (const f of fields) {
    if (
      (f.webhookCheckStatus === "pending" || f.webhookCheckStatus === "unresolved") &&
      f.webhookCheckRuleId
    ) {
      ids.add(f.webhookCheckRuleId);
    }
  }
  return [...ids];
}

/**
 * R-227 — in-flight guard for handleAccept.
 *
 * A single end-of-batch Enter could fire handleAccept TWICE — once from the
 * `formReadyToSubmit` effect and once from KeyboardManager's `onAccept` — and
 * the second invocation, landing while the first's POST /actions was still
 * pending, double-fired POST /actions AND POST /queues/next within ~50ms
 * (confirmed in deployed logs; the second nav also clobbered the first's
 * batch-complete toast — R-226).
 *
 * `runAcceptGuarded` runs `body` only when `guard.current` is false, flips it
 * true for the duration of the returned promise, and resets it on BOTH
 * settle paths (success and error) so the error-toast "Retry" can re-accept.
 * A re-entrant call while a body is still pending is a silent no-op. The
 * guard flag is a `useRef`-backed mutable container so the gate is stable
 * across renders (it does NOT belong in the handleAccept useCallback deps).
 *
 * @internal — exported for unit testing only; not for use outside the verification feature.
 */
export function runAcceptGuarded(
  guard: { current: boolean },
  body: () => Promise<unknown>,
): void {
  if (guard.current) return;
  guard.current = true;
  void Promise.resolve()
    .then(body)
    .finally(() => {
      guard.current = false;
    });
}

/**
 * R-239 P4 Task 6 — the set of field names the VS presented to the operator
 * for this document, reported to the server on accept so recordOutcomes can
 * mark each profile-learning field presented vs skipped (and reset/increment
 * `readsSinceForced`). This IS the smart-skip visit set — the fields below the
 * effective confidence floor that the operator was routed to / could key.
 *
 * It reuses the canonical smart-skip predicate via `computeSmartSkipVisitSet`
 * (and the JT-threshold→type-floors adapter) so the names the server records
 * match exactly what the keying flow stopped on — no reimplementation. Names
 * are the canonical internal names that match `fieldDefinition.internalName`
 * server-side. (Task 7 will extend this set with valve-forced fields.)
 */
export function computeAcceptPresentedFields(
  fields: readonly SmartSkipField[],
  jobTypeVerifyThreshold: number | null,
  policy: StepReverifyPolicy = "standard",
): string[] {
  // F-046 — honor the step policy: under a narrowed policy, fields outside
  // the visit set were genuinely not presented, so reporting them presented
  // would poison the presented-vs-skipped learning record.
  const typeFloors = jobTypeThresholdAsTypeFloors(fields, jobTypeVerifyThreshold);
  return computeSmartSkipVisitSet(fields, typeFloors, policy).visitOrder;
}

/**
 * F-063 — flush the debounced keyed-value persists and await them BEFORE any
 * POST that disposes the document server-side (accept/reject/hold/escalate —
 * all of which snapshot DB keyedValues). Acting right after typing lands
 * inside the 400ms persist debounce window; without this sequencing accept
 * advanced the doc through verify→export against the stale DB keyedValue and
 * the export artifact shipped an empty value with no error (live repro: doc
 * cmr2aw0d…, export cmr33q7ua…). A flush failure rejects WITHOUT posting —
 * the caller's catch surfaces Retry, which is strictly better than disposing
 * a doc whose keyed value never persisted (the failed write re-arms, so
 * Retry re-sends it).
 *
 * @internal — exported for unit testing only; not for use outside the verification feature.
 */
export function postAfterKeyedFlush<T>(url: string, body: unknown): Promise<T> {
  return flushPendingFieldWrites().then(() => api.post<T>(url, body));
}

/**
 * F-095 — THE single validated accept path. Every way a document can be
 * accepted (the `formReadyToSubmit` gate, Ctrl+Enter, Ctrl+Shift+Enter,
 * plain Enter on the last field) MUST route through this function; the
 * explicit gestures previously called the unvalidated `handleAccept`
 * directly, letting an operator accept a document with failing rules or
 * blank required fields.
 *
 * Sequence (the R-127 step-7 / R-239 Slice-3 gate, extracted from the
 * `formReadyToSubmit` effect):
 *   1. flush pending debounced field persists (F-063 — /validate reads DB
 *      keyedValues; a flush failure blocks the submit with a retry toast)
 *   2. run content-native server validation (POST /documents/:id/validate)
 *   3. block + jump the cursor to the first failing field when any field
 *      fails a rule OR is required-and-blank. The required-blank check is
 *      client-side belt-and-suspenders: /validate is fail-open on transport
 *      errors and gestures can fire before the operator visits the field.
 *      Overridden and non-keyable (`isOperatorKeyable` — shared with
 *      `advanceField`'s needsEdit) fields are carved out: the operator
 *      can't key them, so blocking on them would deadlock the document.
 *   4. all clear → onValid() (the accept POST)
 *
 * @internal — exported for unit testing (VerificationStation-submit-gate);
 * production callers go through `useValidatedAccept`.
 */
export async function runValidatedSubmit(onValid: () => void): Promise<void> {
  const currentDocId = useVerificationStore.getState().documentId;
  if (currentDocId) {
    try {
      await flushPendingFieldWrites();
    } catch {
      toast.error("Couldn't save your latest change — check your connection and try again.");
      return;
    }
    // F-036 — validate at the doc's REAL stage, not the literal 'verification':
    // rules authored for key/qa/supervisor now fire where authored (legacy
    // 'verification' rules expand to all edit-capable stages runtime-side).
    const { valid, fieldResults } = await useVerificationStore
      .getState()
      .runContentValidation(currentDocId, stageForStep(useVerificationStore.getState().currentStep));
    const latestFields = useVerificationStore.getState().fields;
    const failing = latestFields.filter(
      (f) =>
        f.validationStatus === "fail" ||
        (f.required && !f.value && !f.isOverridden && isOperatorKeyable(f)),
    );
    if (failing.length > 0) {
      const failIdx = latestFields.indexOf(failing[0]!);
      // E-072 — hand the cursor back for a re-key, not just the index. The
      // blocking field is usually the one the operator was already on, so a
      // bare setActiveField is a no-op: no caret, no selection. requestFieldFocus
      // bumps a nonce that makes FieldInput focus it and select the whole
      // value, so the first keystroke replaces it (the value stays visible
      // until then).
      useVerificationStore.getState().requestFieldFocus(failIdx);
      // F-095 follow-up — the jump alone reads as a mysterious focus hop to
      // an operator who missed the inline red state; the refusal must SAY why.
      toast.error(refusalMessage(failing));
      return;
    }
    if (!valid) {
      // F-038 — server-only refusal. The main way here now: a BLOCK rule
      // failing on an overridden field (the store keeps the field 'overridden'
      // so the client filter above misses it). Name the failing fields from
      // the validate response; keep the generic line only when there is truly
      // nothing to name (form-scope rules carry no field marker).
      const serverFailing = latestFields.filter((f) => fieldResults[f.id]?.valid === false);
      // E-072 — this is a refusal too, so it hands the cursor back the same
      // way the client-side branch does. A named field the operator can't see
      // the caret in is the exact symptom E-072 describes. Form-scope rules
      // carry no field marker; there is nothing to focus then.
      if (serverFailing.length > 0) {
        useVerificationStore
          .getState()
          .requestFieldFocus(latestFields.indexOf(serverFailing[0]!));
      }
      toast.error(
        serverFailing.length > 0
          ? refusalMessage(serverFailing)
          : "Can't accept — validation failed. Check the document's rules.",
      );
      return;
    }
    // F-023 — standing warnings require a one-time acknowledgment: pause the
    // accept ONCE, list them, and let the operator confirm or go review. Acked
    // fields never re-prompt on resubmit (per-doc, reset on doc load).
    const { acknowledgedWarnings } = useVerificationStore.getState();
    const unackedWarns = latestFields.filter(
      (f) => f.validationStatus === "warn" && !acknowledgedWarnings.has(f.id),
    );
    if (unackedWarns.length > 0) {
      useVerificationStore.getState().setPendingWarnAck({
        fields: unackedWarns.map((f) => ({
          id: f.id,
          label: f.label || f.name,
          message: f.validationMessage,
        })),
      });
      return;
    }
  }
  onValid();
}

/** F-095 follow-up — "Can't accept — 2 fields need attention: Alpha, Bravo". */
function refusalMessage(failing: Array<{ label: string; name: string }>): string {
  const names = failing.slice(0, 3).map((f) => f.label || f.name);
  const overflow = failing.length > 3 ? ` (+${failing.length - 3} more)` : "";
  const noun = failing.length === 1 ? "field needs" : "fields need";
  return `Can't accept — ${failing.length} ${noun} attention: ${names.join(", ")}${overflow}`;
}

/**
 * F-095 — stable callback wrapper around `runValidatedSubmit`, wired as
 * KeyboardManager's `onAccept` AND the `formReadyToSubmit` effect body so
 * every accept entry point shares one gate.
 */
export function useValidatedAccept(onAccept: () => void): () => void {
  return useCallback(() => {
    void runValidatedSubmit(onAccept);
  }, [onAccept]);
}

/**
 * R-189 — the server-authoritative response from
 * POST /documents/:id/actions {action:"accept"}. The client trusts these
 * fields instead of polling GET /batches/:id for a "ready" sibling (the
 * stale-cache race that produced the phantom-doc blank canvas).
 */
export interface AcceptResponse {
  nextDocumentId: string | null;
  batchComplete: boolean;
  batchAdvancedTo: string | null;
  batchAutoReleased: boolean;
  // R-162 §10.1 — set when the operator's continuous-feed lane was blocked by a
  // supervisor while they keyed this doc. Response-only fields (the server adds
  // them at the route, not in BatchAdvanceState).
  feedBlocked?: boolean;
  blockReason?: string | null;
}

/**
 * The terminal the operator lands on once a batch is exhausted: the
 * feed-exhausted session debrief when a continuous-feed lock was active,
 * otherwise the queue dashboard.
 */
type AcceptFallback = "queue" | "feed-exhausted";

/**
 * F-043 — should the post-accept terminal path pull the NEXT item via
 * /queues/next, or go straight to the terminal route?
 *
 * Re-feeding is the *continuous-feed* behavior: only when the operator holds a
 * feed lock (the "feed-exhausted" terminal) does the VS claim the next item and
 * roll into it. With no feed lock (the plain "queue" terminal) the operator
 * opened a single doc/batch — accepting the last ready doc must close the batch
 * and return to /queues, NOT auto-feed a phantom / cross-batch doc.
 *
 * @internal — exported for unit testing only.
 */
export function shouldRefeedAfterAccept(fallback: AcceptFallback): boolean {
  return fallback === "feed-exhausted";
}

/**
 * The single decision handleAccept executes after an accept POST resolves.
 * - navigate: a concrete next doc exists in this batch → go straight there.
 * - complete: the operator keyed the last doc → show a completion toast, then
 *   try the queue and land on the `fallback` terminal.
 * - fallback: nothing to navigate to and no completion (e.g. a sibling is
 *   owned by another operator) → quietly try the queue / land on `fallback`.
 */
export type AcceptOutcome =
  | { kind: "navigate"; to: string }
  | { kind: "complete"; toast: string; fallback: AcceptFallback }
  | { kind: "fallback"; fallback: AcceptFallback }
  // R-162 §10.1 — the operator's feed lane was blocked mid-batch. The VS pops
  // the QueueBlockedModal + stops the feed (wired in WU-3.7).
  | { kind: "blocked"; reason: string };

/**
 * Pure post-accept decision (R-189). Maps the server verdict + the operator's
 * feed-lock state to a single outcome. No speculative batch poll — the server
 * already resolved the next doc / batch-complete state authoritatively.
 */
export function resolveAcceptOutcome(
  resp: AcceptResponse,
  ctx: { feedLock: boolean; batchLabel: string; batchDocCount: number },
): AcceptOutcome {
  // R-162 §10.1 — a supervisor block takes precedence over everything else:
  // the operator must be diverted off the parked lane even if a next doc exists.
  if (resp.feedBlocked) {
    return { kind: "blocked", reason: resp.blockReason ?? "Queue blocked" };
  }

  if (resp.nextDocumentId) {
    return { kind: "navigate", to: `/verify/${resp.nextDocumentId}` };
  }

  const fallback: AcceptFallback = ctx.feedLock ? "feed-exhausted" : "queue";

  if (resp.batchComplete) {
    const docNoun = ctx.batchDocCount === 1 ? "doc" : "docs";
    const disposition = resp.batchAutoReleased ? "released to export" : "ready";
    const toast = `Batch ${ctx.batchLabel} complete — ${ctx.batchDocCount} ${docNoun} ${disposition}`;
    return { kind: "complete", toast, fallback };
  }

  return { kind: "fallback", fallback };
}

export type RefeedOutcome =
  | { kind: "navigate"; to: string }
  // WU-2.4 T8 — an eviction refused this re-claim. Mirrors resolveAcceptOutcome's
  // "blocked" kind (the supervisor feedBlocked case): divert to QueueBlockedModal
  // instead of silently landing on the terminal, which would read as "queue
  // empty" rather than "you were evicted". `cause: "evicted"` (WU-2.4
  // /simplify) lets the call site pick QueueBlockedModal's eviction copy
  // instead of the supervisor-block copy — this outcome is ALWAYS
  // eviction-caused (see below), so the literal cause never varies.
  | { kind: "blocked"; reason: string; cause: "evicted" }
  | { kind: "terminal" };

/**
 * WU-2.4 T8 — pure decision for the continuous-feed re-claim POST
 * `/queues/next` fired from `landOnTerminal` below (same "extract for
 * testability without mounting canvas/OCR" pattern as `resolveAcceptOutcome`
 * above; response contract is queue-claim's `GetNextResponse` — the same
 * route). An eviction-caused claim refusal must be treated exactly like the
 * parked-lane case: the operator is diverted to QueueBlockedModal, NOT
 * silently dropped onto the plain terminal route.
 *
 * WU-2.4 /simplify — `reason` carries the RAW eviction reason (not the
 * `formatEvictionNotice` sentence). QueueBlockedModal is now cause-aware
 * (`cause: "evicted"`) and supplies its own eviction framing ("A supervisor
 * has moved you off this queue." + "Reason: <raw>"); pre-formatting here
 * produced a stacked, contradictory modal when combined with the modal's
 * own copy.
 *
 * @internal — exported for unit testing only.
 */
export function resolveRefeedOutcome(resp: GetNextResponse): RefeedOutcome {
  if (resp.available && resp.document) {
    return { kind: "navigate", to: `/verify/${resp.document.documentId}` };
  }
  if (resp.reason === "evicted") {
    return { kind: "blocked", reason: resp.evictionReason ?? "", cause: "evicted" };
  }
  return { kind: "terminal" };
}

/**
 * R-235 B2 — Payload published by the server on `queue:<tenantId>` when a
 * supervisor/admin moves a document from one pipeline step to another.
 * This is the UNWRAPPED payload (wire envelope is `{ type, payload }` —
 * see WsClient.onPayload, B1 ff-web).
 */
interface DocumentMovedPayload {
  documentId: string;
  toStep: string;
  operatorId: string;
}

/**
 * R-235 B2 — Graceful-bump hook: subscribes to `document_moved` WS events
 * and, when the moved documentId matches the operator's current doc,
 * shows a non-blocking info toast and navigates to the queue fallback.
 *
 * The in-progress unsaved entry is dropped — no /actions or keystroke-snapshot
 * POST fires as a result of the bump. The caller (VerificationStation) sets a
 * `bumpedRef` flag before calling navigate so VerificationKeystrokeRecorder's
 * unmount flush is skipped for the bumped doc.
 *
 * Exported for unit testing only (same pattern as runAcceptGuarded).
 *
 * @param opts.currentDocumentId - the documentId param from the current route
 * @param opts.feedLock - whether the operator has a continuous-feed lock active
 * @param opts.onBump - optional callback invoked just before navigation (used
 *   by the VS to set its bumpedRef flag, preventing the keystroke-snapshot flush)
 */
export function useDocumentMovedBump(opts: {
  currentDocumentId: string | undefined;
  feedLock: boolean;
  onBump?: () => void;
}): void {
  const { currentDocumentId, feedLock, onBump } = opts;
  const wsClient = useWsClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!wsClient || !currentDocumentId) return;

    const off = wsClient.onPayload("document_moved", (raw) => {
      const msg = raw as DocumentMovedPayload;
      if (msg.documentId !== currentDocumentId) return;

      // Notify the VS before navigation so it can set the bumpedRef guard
      onBump?.();

      const stepLabel = formatStep(msg.toStep);
      toast.info(
        `This document was moved by a supervisor → ${stepLabel}. Returning you to your queue.`,
      );

      const terminalPath = feedLock ? "/queues/feed-exhausted" : "/queues";
      navigate(terminalPath);
    });

    return off;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsClient, currentDocumentId, feedLock]);
}

/**
 * WU-2.4 T8 — the F-063 data-loss guarantee applied to immediate evictions.
 *
 * `after_document`/`after_batch` evictions are enforced at claim-time
 * (Tasks 6/7 — the current doc is allowed to finish normally) so this hook
 * does nothing for those modes. An `immediate` eviction whose stepType/
 * jobTypeId match the doc currently open in the VS means the operator must
 * leave RIGHT NOW — but leaving mid-debounce would silently drop the last
 * keystroke, exactly the race F-063 closed for accept/reject/hold/escalate
 * (see `postAfterKeyedFlush` above). So this handler flushes every pending
 * debounced field/line-item-cell persist and AWAITS it before showing the
 * reason toast and navigating to /queues.
 *
 * Match rule (mirrors the server's release scope in executeEviction):
 *   - `stepType` must equal the doc's current step.
 *   - `jobTypeId === null` on the payload is a step-wide eviction (matches
 *     every lane of that step); otherwise it must equal the doc's jobTypeId.
 *
 * The server's `executeEviction` already releases the operator's held
 * document(s) in scope for `immediate` mode (queueManager.releaseDocument) —
 * this doc is no longer this operator's assignment by the time the WS event
 * lands. `opts.onEvict` lets the caller (VerificationStation) raise its
 * `releaseSuppressRef` before navigate() fires, the same F-054 guard every
 * other disposal path (accept/reject/hold/escalate/document_moved bump)
 * already sets — without it, `useReleaseOnExit`'s unmount cleanup would fire
 * a redundant (or actively harmful, if a new operator already claimed the
 * doc) `DELETE /documents/:id/lock`.
 *
 * Exported for unit testing only (same pattern as useDocumentMovedBump).
 */
export function useQueueEvictionExit(opts: {
  currentStep: string;
  jobTypeId: string | null;
  onEvict?: () => void;
}): void {
  const { currentStep, jobTypeId, onEvict } = opts;
  const wsClient = useWsClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!wsClient) return;

    // The payload is the canonical `queue_eviction` wire shape —
    // QueueEvictionEvent (queue-evictions/api/client.ts), published by
    // services/queue-eviction.ts publishEviction on the operator's PRIVATE
    // user channel; onPayload has already unwrapped the { type, payload }
    // envelope (same as DocumentMovedPayload above).
    return wsClient.onPayload("queue_eviction", (raw) => {
      const p = raw as QueueEvictionEvent;
      if (p.mode !== "immediate") return;
      if (p.stepType !== currentStep) return;
      if (p.jobTypeId !== null && p.jobTypeId !== jobTypeId) return;

      void (async () => {
        // F-063 — the doc on screen is about to be abandoned; flush pending
        // keyed-value writes and await them BEFORE navigating away, or a
        // keystroke typed just before the eviction lands is silently dropped.
        try {
          await flushPendingFieldWrites();
        } catch {
          // Unlike accept (where a failed flush blocks the POST to protect
          // the DB snapshot), the eviction is ALREADY committed server-side —
          // blocking the exit protects nothing and would strand the operator
          // on a doc they no longer own, with no notice. The failed write
          // re-armed its own debounce timer (module-level state, survives
          // navigation) so it retries regardless of the route.
        }
        // F-054 — the doc is already released server-side (see docstring
        // above); suppress the exit-release BEFORE navigate unmounts the VS.
        onEvict?.();
        // The Toaster's info()/error() auto-dismiss after a few seconds — too
        // short for a message the operator needs to actually read and act on
        // ("why did I just get bounced?"). persistent() has no auto-dismiss
        // timer and keeps its explicit × close button, so it stays until the
        // operator reads and dismisses it.
        toast.persistent("info", formatEvictionNotice(p.reason));
        navigate("/queues");
      })();
    });
  // onEvict intentionally omitted (same convention as useDocumentMovedBump's
  // onBump): it's read fresh via the closure at event-time, not deps-tracked.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsClient, navigate, currentStep, jobTypeId]);
}

/**
 * Phase 20/C3b — one LineItemGrid per LineItemDefinition on the current doc's
 * job type. No-op when the job type declares no line-item definitions (most
 * receipt / warranty types). Columns are read from the definition's JSON
 * `columns` field; rows are keyed by `lineItemDefId` in the store.
 */
function LineItemGridsMount() {
  const definitions = useVerificationStore((s) => s.lineItemDefinitions);
  const lineItems = useVerificationStore((s) => s.lineItems);

  if (!definitions || definitions.length === 0) return null;

  return (
    <>
      {/* P2b — per-row rule-check failures summarized above the grids. */}
      <RowCheckSummary />
      {definitions.map((def) => {
        const columns = normalizeColumnsForGrid(def.columns);
        if (columns.length === 0) return null;
        const rows = lineItems[def.id] ?? [];
        return (
          <LineItemGrid
            key={def.id}
            groupId={def.id}
            groupName={def.name}
            columns={columns}
            rows={rows}
          />
        );
      })}
    </>
  );
}

function normalizeColumnsForGrid(raw: unknown): LineItemColumn[] {
  if (!Array.isArray(raw)) return [];
  const out: LineItemColumn[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const obj = entry as Record<string, unknown>;
    if (typeof obj["name"] !== "string") continue;
    out.push({
      name: obj["name"],
      label: (obj["label"] as string | undefined) ?? obj["name"],
      dataType: (obj["dataType"] as string | undefined) ?? "text",
      width: obj["width"] as string | undefined,
      isCalculated: obj["isCalculated"] === true,
      formula: obj["formula"] as string | undefined,
    });
  }
  return out;
}

/** Prefetch the next few documents from the queue in the background. */
async function prefetchNextDocuments(
  prefetch: (id: string, priority: "high" | "medium" | "low") => Promise<void>,
) {
  try {
    const queue = await api.get<Array<{ documentId: string }>>("/queues/peek?limit=3");
    if (!Array.isArray(queue)) return;
    for (let i = 0; i < queue.length; i++) {
      const item = queue[i];
      if (item?.documentId) {
        const priority = i === 0 ? "high" : i === 1 ? "medium" : "low";
        // Fire and forget — don't await, these load in the background
        prefetch(item.documentId, priority).catch(() => {});
      }
    }
  } catch {
    // Queue peek failed — prefetch is best-effort, no user impact
  }
}

export function VerificationStation() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const loadDocument = useVerificationStore((s) => s.loadDocument);
  const isLoading = useVerificationStore((s) => s.isLoading);
  const batchName = useVerificationStore((s) => s.batchName);
  // E-050 — the header/transition/toast identity is the shared preferred
  // label (customDisplayLabel ?? displayId ?? name), NOT the filename-derived
  // free-text `name` (same-named seed batches were indistinguishable).
  const batchLabel = useVerificationStore(selectBatchLabel);
  const filename = useVerificationStore((s) => s.filename);
  // R-167 WU-7 — frozen-label-aware identifier for the VS header. Operators
  // see "INV-2026-0042 — Acme.pdf" instead of "Acme.pdf" alone, which makes
  // it possible to triangulate the doc against BatchList / BatchDetail.
  const docDisplayId = useVerificationStore((s) => s.displayId);
  const docCustomDisplayLabel = useVerificationStore((s) => s.customDisplayLabel);
  const acceptDocument = useVerificationStore((s) => s.acceptDocument);
  const jobTypeId = useVerificationStore((s) => s.jobTypeId);
  const terminalMode = useVerificationStore((s) => s.terminalMode);
  const incrementSessionStat = useVerificationStore((s) => s.incrementSessionStat);
  const currentStep = useVerificationStore((s) => s.currentStep);
  const { prefetch, getDocument, evict } = usePrefetch();
  const { user } = useAuth();
  // R-162 — operator's continuous-feed lock. Drives the exhausted-feed
  // terminal in handleAccept and the cross-batch helper below.
  const { lock: feedLock, stop: stopFeed } = useFeedLock();
  const batchIdForTransition = useVerificationStore((s) => s.batchId);

  const transition = useBatchTransitionEffect({
    currentBatchId: batchIdForTransition,
    // E-050 — cross-batch modal/toast lead with the unique batch identity
    // (was `batchName || batchId`, which rendered same-named seed batches
    // as an incoherent "de1545_test_10 → de1545_test_10" transition).
    currentBatchDisplayId: batchLabel || batchIdForTransition,
    currentStep: currentStep ?? "",
    onFlowTransition: (label) => {
      toast.info(`Now on Batch ${label}`);
    },
    onQcTransitionAccept: () => {
      // No-op — the post-Accept auto-advance already navigated to the new doc.
    },
    onQcTransitionCancel: () => {
      void stopFeed();
      navigate("/queues");
    },
  });

  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);

  // R-247 5b-3b — keyer "Wrong document type" doc-level action. Gated on the
  // escalate-from permission; the dialog collects an optional "what should it
  // be?" note before POSTing to /documents/:id/escalate.
  const [misidentOpen, setMisidentOpen] = useState(false);
  // R-247 5b-3b — in-flight guard so the Retry path (which reopens the dialog)
  // can't double-fire the escalate POST. Refs are stable → NOT a useCallback dep.
  const flaggingRef = useRef(false);
  const { checkPermission, hasPermission } = usePermissions();
  useEffect(() => {
    void checkPermission("escalate-from");
  }, [checkPermission]);
  const canFlagMisidentified = hasPermission("escalate-from");
  // R-162 §10.1 — set when an accept response carries feedBlocked: the operator
  // finished the in-flight doc but a supervisor parked their feed lane. Holds
  // the supervisor's reason for the QueueBlockedModal.
  //
  // WU-2.4 /simplify — `queueBlockedCause` is a parallel state (not folded
  // into an object) because the two call sites below set them from two
  // distinct outcome types (AcceptOutcome's "blocked" is always
  // supervisor-caused; RefeedOutcome's "blocked" is always eviction-caused) —
  // each call site sets both together, so they never drift.
  const [queueBlockedReason, setQueueBlockedReason] = useState<string | null>(null);
  const [queueBlockedCause, setQueueBlockedCause] = useState<"blocked" | "evicted">("blocked");

  // R-239 B4 — Ctrl+O override dialog. Null = closed; set carries the field +
  // resolved policy for the OverrideDialog (override or escalate mode).
  const [overrideDialog, setOverrideDialog] = useState<{
    fieldId: string;
    mode: "override" | "escalate";
    requireReason: boolean;
    ruleName: string;
    fieldLabel: string;
    currentValue: string;
    fallbackValue?: string;
    escalationQueue?: string;
    // E-043 — the resolved human failure message (custom, else a config
    // one-liner for default-message rules), carried to the audit trail
    // alongside ruleName/fieldLabel.
    ruleMessage?: string;
    /**
     * S-5 Task 7 — 'webhook' opens for an unresolved any-timing webhook
     * check (onUnresolved 'soft'/'escalate' — resolveWebhookUnresolvedAction),
     * not a field-rule override. Reuses this SAME dialog + state (design §5
     * "existing override dialog" / "existing escalate flow"), but
     * `handleOverrideDialogConfirm` branches on it: there is no
     * persist-override endpoint for a webhook check yet (Task 6 didn't add
     * one — 'soft' already lets accept PROCEED server-side once
     * reconciled), so confirming here is a client-side audit gate + accept
     * retry, not a `doPersistOverride`/`escalateField` call against
     * `fieldId` (which would be a bogus empty-string field id). Absent =
     * the existing field-rule flow, byte-identical to before this task.
     */
    source?: "webhook";
  } | null>(null);

  // S-5 Task 7 — accept-boundary "wait and retry" (design §4/§5): set when
  // the accept POST 409s with the Task 6 `checks_pending` shape. Renders the
  // boundary spinner in place of the error toast; cleared on a resolving WS
  // event (auto-retry) or a successful accept.
  const [webhookBoundary, setWebhookBoundary] = useState<{ pendingChecks: number } | null>(null);

  // S-5 Task 7 — onUnresolved:'hold' banner (design §5 "batch banner 'held —
  // unresolved external checks'"). No pre-existing "batch held" component
  // was found to reuse (BatchDetailPage's DocErrorPopover is the batch-LIST
  // error affordance, not a VS-mounted banner; QueueBlockedModal's two
  // causes ["blocked", "evicted"] both carry copy that doesn't fit a
  // webhook-hold — see task-7-report.md) — this is a small new inline
  // banner using the design spec's own copy.
  const [webhookHoldBanner, setWebhookHoldBanner] = useState<{ ruleName: string } | null>(null);

  // S-5 Task 7 review round 2, item 2 — the operator's escape hatch behind
  // the boundary spinner: force a fresh dial on every currently pending/
  // unresolved check via the existing (previously unreachable) refire
  // route, instead of waiting out PENDING_GRACE_MS. Best-effort per rule —
  // one failed refire must not stop the others; the WS webhook_check_update
  // stream (or the next accept attempt) remains the actual source of truth.
  const handleRetryAllPending = useCallback(() => {
    const state = useVerificationStore.getState();
    const docId = state.documentId;
    if (!docId) return;
    for (const ruleId of collectPendingWebhookRuleIds(state.fields)) {
      void api.post(`/documents/${docId}/webhook-checks/${ruleId}/refire`).catch(() => {});
    }
  }, []);

  // Apply operator preferences from localStorage to store on mount
  useApplyPreferences();

  // Detachable field panel
  const { isDetached, detach, reattach } = useDetachablePanel();

  // R-239 Plan B (B3.T2a.2) — published content `validate` rules, fetched
  // per-jobType, evaluated client-side on blur by FieldInput (no server hop).
  // F-036 — fetched for the doc's REAL stage so blur checks mirror what the
  // accept-gate server pass enforces at this step.
  const { rules: contentValidateRules, lookupData: contentLookupData, constants: contentConstants } = useContentValidateBundle(
    jobTypeId,
    stageForStep(currentStep),
  );

  // R-239 B4 — persist an override (server audits + step-gates). On success
  // mark the field overridden locally + clear its trap toast; a 403 means the
  // operator lacks override permission at this step (defect #4 enforcement).
  const doPersistOverride = useCallback(
    async (
      fieldId: string,
      reason: string,
      fallbackValue?: string,
      context?: OverrideAuditContext,
    ) => {
      const docId = useVerificationStore.getState().documentId;
      if (!docId) return;
      const { ok, forbidden } = await persistFieldOverride(
        docId,
        fieldId,
        reason,
        fallbackValue,
        context,
      );
      if (ok) {
        useVerificationStore.getState().overrideField(fieldId, reason || "(no reason given)");
        if (fallbackValue !== undefined) {
          useVerificationStore.getState().setFieldValue(fieldId, fallbackValue);
        }
        toast.success("Field overridden");
      } else if (forbidden) {
        toast.error("You can't override at this step.");
      } else {
        toast.error("Override failed — try again.");
      }
    },
    [],
  );

  // R-239 B4 — Ctrl+O entry point. Resolves the active failing field's override
  // policy and either refuses (block), quick-confirms (override, no reason),
  // opens the dialog (override w/ reason, or escalate), per resolveOverrideAction.
  const handleOverride = useCallback(() => {
    const state = useVerificationStore.getState();
    const field = state.fields[state.activeFieldIndex];
    if (!field || field.validationStatus !== "fail") return;

    // F-037 — the override policy belongs to the binding actually failing on
    // this value; pickActiveEntry is the same winner selection the blur
    // evaluator uses, so the two can never drift. Task 12b — same fields
    // snapshot as the blur evaluator, so an authored branch-IF resolves
    // identically here.
    const valuesByName: Record<string, string> = {};
    const confidenceByName: Record<string, number> = {};
    for (const f of state.fields) {
      valuesByName[f.name] = f.value;
      if (f.ocrConfidence != null) confidenceByName[f.name] = f.ocrConfidence;
    }
    // E-043 — pickActiveEntry's `.message` (the rule's human failure text)
    // used to be discarded here; it's now threaded into the audit context
    // so the override trail can say WHAT failed, not just "field".
    // P1 — same ctx the blur evaluator builds (values + confidence + lookup),
    // so the override-policy winner can never drift from the blur winner.
    const picked = pickActiveEntry(contentValidateRules[field.name] ?? [], field.value, {
      fields: valuesByName,
      fieldConfidence: confidenceByName,
      lookupData: contentLookupData,
      constants: contentConstants,
    });
    const entry = picked?.entry;
    const behavior = entry?.overrideBehavior ?? field.overrideBehavior;
    const action = resolveOverrideAction(behavior, entry?.overrideConfig);
    const ruleName = entry?.rule?.type ?? "validation";
    // E-043 follow-up — resolveOverrideRuleMessage folds a config one-liner
    // ("must be greater than 0") in for default-message rules, so the trail
    // says what the check required rather than a bland per-type default.
    const ruleMessage = resolveOverrideRuleMessage(entry?.rule, picked?.message);
    const auditContext: OverrideAuditContext = {
      fieldLabel: field.label,
      ruleType: ruleName,
      ruleMessage,
    };

    if (action.kind === "refuse") {
      toast.error("This field can't be overridden — fix it.");
      return;
    }
    if (action.kind === "quick") {
      void doPersistOverride(field.id, "", action.fallbackValue, auditContext);
      return;
    }
    // dialog (override w/ reason) or escalate — both open the OverrideDialog,
    // differing only in mode and whether a fallbackValue applies.
    setOverrideDialog({
      fieldId: field.id,
      mode: action.kind === "dialog" ? "override" : "escalate",
      requireReason: action.requireReason,
      ruleName,
      fieldLabel: field.label,
      currentValue: field.value,
      fallbackValue: action.kind === "dialog" ? action.fallbackValue : undefined,
      escalationQueue: action.escalationQueue,
      ruleMessage,
    });
  }, [contentValidateRules, contentLookupData, contentConstants, doPersistOverride]);

  // S-5 Task 7 — handleAccept is defined further below (it needs state
  // declared up here, e.g. webhookBoundary), but handleOverrideDialogConfirm
  // (right below) and the webhook-check onResolved callback (wired near
  // handleAccept's definition) both need to call it. A ref sidesteps the
  // forward-reference: set once handleAccept exists, read by both.
  const handleAcceptRef = useRef<() => void>(() => {});

  // R-239 B4 — dialog confirm. Override mode persists; escalate mode is a local
  // stub (the /escalate field endpoint is Plan D) that routes via escalateField.
  const handleOverrideDialogConfirm = useCallback(
    (reason: string) => {
      const dlg = overrideDialog;
      if (!dlg) return;
      if (dlg.source === "webhook") {
        // S-5 Task 7 review round 2, item 1 — the REAL override write
        // (design §2 "reason + audit"), not a toast-only stub: persists via
        // the SAME endpoint + audit model the field-rule Ctrl+O override
        // uses (persistWebhookOverride -> persistFieldOverride ->
        // POST /documents/:id/override), tagged ruleType:"webhook_check".
        // canAdvance's onUnresolved:'soft' branch already lets accept
        // PROCEED unconditionally once reconciled (design §4 case 7) — the
        // write here is the audit record design §2 requires, not a token
        // the accept route reads back. Accept is retried ONLY on a
        // successful write; a 403/failure leaves the dialog's effect
        // undone and the operator un-accepted, with an actionable toast.
        const docId = useVerificationStore.getState().documentId;
        if (dlg.mode === "override") {
          if (!docId || !dlg.fieldId) {
            toast.error("Override failed — try again.");
            setOverrideDialog(null);
            return;
          }
          setOverrideDialog(null);
          void persistWebhookOverride(docId, dlg.fieldId, reason, dlg.ruleName, dlg.ruleMessage).then(
            ({ ok, forbidden }) => {
              if (ok) {
                useVerificationStore.getState().overrideField(dlg.fieldId, reason || "(no reason given)");
                toast.success("Override recorded — retrying accept");
                handleAcceptRef.current();
              } else if (forbidden) {
                toast.error("You can't override at this step.");
              } else {
                toast.error("Override failed — try again.");
              }
            },
          );
          return;
        }
        // S-5 Task 7 — escalate mode: canAdvance's onUnresolved:'escalate'
        // branch (workflow-engine.ts) performs the REAL escalateDocument()
        // as a side effect of the NEXT accept attempt (it needs a real
        // userId + the accept boundary's own DB transaction, neither of
        // which this dialog confirm has) — same "acknowledge, then let the
        // next accept enact it" posture as the pre-existing escalateField
        // branch below (its own comment: "the /escalate field endpoint is
        // Plan D"). Retrying accept here is what actually TRIGGERS it.
        toast.info("Escalating to supervisor — retrying accept");
        setOverrideDialog(null);
        handleAcceptRef.current();
        return;
      }
      if (dlg.mode === "override") {
        void doPersistOverride(dlg.fieldId, reason, dlg.fallbackValue, {
          fieldLabel: dlg.fieldLabel,
          ruleType: dlg.ruleName,
          ruleMessage: dlg.ruleMessage,
        });
      } else {
        useVerificationStore.getState().escalateField(dlg.fieldId, reason);
        toast.info("Escalated to supervisor");
      }
      setOverrideDialog(null);
    },
    [overrideDialog, doPersistOverride],
  );

  // R-32 P2: load the per-Job-Type verify threshold into the store so
  // tab-skip + highlight resolve per-JT (fixes the never-populated bug).
  const setVerifyThreshold = useVerificationStore((s) => s.setVerifyThreshold);
  const { data: currentJobType } = useCurrentJobType(jobTypeId);
  const jtVerifyThreshold = currentJobType?.verifyThreshold ?? null;
  useEffect(() => {
    setVerifyThreshold(jtVerifyThreshold);
  }, [jtVerifyThreshold, setVerifyThreshold]);

  useEffect(() => {
    if (!documentId) return;

    // R-140 — flip isLoading=true at the start of every documentId change
    // so the spinner shows instead of the stale-or-empty render that
    // happens when navigating doc→doc (the store carried isLoading=false
    // from the prior load, and the JSX rendered against whatever fields
    // were left in the store until the new fetch resolved → read as a
    // "blank white screen" until reload kicked the effect again).
    useVerificationStore.setState({ isLoading: true });

    // Check if this document was pre-loaded in the prefetch cache
    const cached = getDocument(documentId);
    if (cached && cached.status === "ready" && cached.payload) {
      // Use cached payload — near-instant transition
      loadDocument(cached.payload);

      // R-239 Slice 3 — load-time pre-validation. Runs AFTER loadDocument has
      // put the new doc's fields into the store, so it validates the just-loaded
      // doc (not the previous one). `silent` → no RevalidationOverlay flash on
      // open; fail-open inside the thunk → never blocks loading.
      void useVerificationStore
        .getState()
        .runContentValidation(
          documentId,
          stageForStep(useVerificationStore.getState().currentStep),
          { silent: true },
        );

      // Apply cached ImageBitmaps to the store
      for (const [pageNumber, bitmap] of cached.imageBitmaps) {
        const pageIndex = cached.payload.pages.findIndex(
          (p) => p.pageNumber === pageNumber
        );
        if (pageIndex >= 0) {
          useVerificationStore.getState().setPageBitmap(pageIndex, bitmap);
        }
      }

      // Prefetch next documents from queue
      prefetchNextDocuments(prefetch);
      return;
    }

    // Fallback: fetch from API (existing behavior)
    api.get<ApiDocument>(`/documents/${documentId}`).then((doc) => {
      const payload = mapApiToPayload(doc);
      loadDocument(payload);

      // R-239 Slice 3 — load-time pre-validation (see cached-path note above).
      // Runs after loadDocument so it validates the doc just fetched.
      void useVerificationStore
        .getState()
        .runContentValidation(
          documentId,
          stageForStep(useVerificationStore.getState().currentStep),
          { silent: true },
        );

      // Pre-decode page images into ImageBitmap for instant canvas rendering
      payload.pages.forEach((page, idx) => {
        if (page.imageUrl) {
          fetch(page.imageUrl)
            .then((r) => r.blob())
            .then((blob) => createImageBitmap(blob))
            .then((bitmap) => {
              useVerificationStore.getState().setPageBitmap(idx, bitmap);
            })
            .catch(() => {});
        }
      });

      // Prefetch next documents from queue
      prefetchNextDocuments(prefetch);
    }).catch((err) => {
      // R-140 — a rejected document fetch used to leave the user staring at
      // the loading spinner forever (or, when CSS was missing, at a blank
      // screen). Surface the failure and bounce back to the queue so the
      // operator can pick a different doc instead of hitting refresh.
      useVerificationStore.setState({ isLoading: false });
      toast.error(
        `Couldn't load this document: ${err instanceof Error ? err.message : "unknown error"}`,
      );
      navigate("/queues");
    });
  }, [documentId, loadDocument, getDocument, prefetch, navigate]);

  const formReadyToSubmit = useVerificationStore((s) => s.formReadyToSubmit);

  const totalDocsForAccept = useVerificationStore((s) => s.totalDocs);

  // R-227 — in-flight guard so a single Enter can't double-fire handleAccept
  // (the formReadyToSubmit effect AND KeyboardManager.onAccept both call it).
  // A re-entrant call while the accept POST is pending is ignored; the guard
  // resets on success and error. Refs are stable → NOT a useCallback dep.
  const acceptInFlight = useRef(false);

  // R-235 B2 — bump guard. Passed to VerificationKeystrokeRecorder as
  // `skipFinalFlushRef`. When a document_moved event lands for the current
  // doc, useDocumentMovedBump's onBump sets `bumpedRef.current = true`
  // SYNCHRONOUSLY before navigate(). The navigation unmounts the VS, which
  // runs the recorder's unmount-flush cleanup — and that cleanup reads
  // `skipFinalFlushRef.current` (fresh, because it was set after the last
  // render) and returns early, so the in-progress keystroke-snapshot POST
  // is dropped. A plain boolean prop would be stale at cleanup time; the ref
  // is the only way the freshly-set value reaches the cleanup closure.
  const bumpedRef = useRef(false);

  // F-054 (Q-b pt2) — INSTANT release-on-exit. Leaving the VS for ANY reason
  // (route-away, unmount, tab close) must release the current doc's lock +
  // assignment immediately, instead of waiting ~1–2 min for the heartbeat TTL
  // to expire + the server sweep (Q-b pt1) to revert the status.
  //
  // releaseSuppressRef gates the unmount-release for INTENTIONAL exits where the
  // doc was already disposed server-side (accept / reject / hold / flag / a
  // supervisor bump) — re-releasing those would be wrong (the doc moved on) or a
  // wasted call. Advance-to-next is naturally safe (it only swaps the route
  // param; the station stays mounted, so the unmount cleanup doesn't run), but
  // the flag also future-proofs against a keyed-route remount. Plain route-away
  // / tab-close leaves the flag false → the release fires.
  const releaseSuppressRef = useRef(false);
  // Re-arm on every new doc: a fresh doc starts un-suppressed (the prior doc's
  // disposition shouldn't leak into the next one when the station is reused).
  useEffect(() => {
    releaseSuppressRef.current = false;
  }, [documentId]);
  // terminalMode = station no longer owns/keys the doc → don't release.
  useReleaseOnExit({
    documentId,
    active: !terminalMode,
    suppressRef: releaseSuppressRef,
  });

  // R-235 B2 — subscribe to document_moved WS events. When a supervisor moves
  // the doc the operator is currently keying, show a non-blocking toast and
  // navigate to the queue fallback. The in-progress unsaved entry is dropped.
  useDocumentMovedBump({
    currentDocumentId: documentId,
    feedLock: Boolean(feedLock),
    onBump: () => {
      bumpedRef.current = true;
      // F-054 — a supervisor moved this doc; the bump navigates to the queue
      // and the doc already left this operator. Suppress the exit-release.
      releaseSuppressRef.current = true;
    },
  });

  // WU-2.4 T8 — subscribe to queue_eviction WS events. An `immediate`
  // eviction matching this doc's step/jobTypeId lane flushes pending field
  // persists (F-063), then shows the reason and navigates to /queues.
  useQueueEvictionExit({
    currentStep,
    jobTypeId,
    onEvict: () => {
      // F-054 — the eviction already released this doc server-side
      // (executeEviction, immediate mode); suppress the exit-release so
      // unmount doesn't re-release it (or worse, a doc a new operator
      // already claimed).
      releaseSuppressRef.current = true;
      // Deliberately NOT setting bumpedRef here (unlike the document_moved
      // bump above): the keying the operator did up to the eviction is real
      // work worth recording, so the keystroke-snapshot unmount flush should
      // still fire. The bump drops it because the DOC left the operator
      // mid-entry; here the OPERATOR is leaving a doc they legitimately keyed.
    },
  });

  const handleAccept = useCallback(() => {
    if (!documentId) return;
    // R-227 — guard the whole accept so a single Enter can't double-fire it.
    // runAcceptGuarded runs the body only when no accept is in flight and
    // resets the flag when the returned promise (the /actions chain) settles.
    runAcceptGuarded(acceptInFlight, () => {
      // R-89 — snapshot field state so a genuine accept rejection can roll
      // back the optimistic pending→pass flip instead of stranding a
      // half-accepted UI with no feedback.
      const fieldsBeforeAccept = useVerificationStore.getState().fields;
      // F-054 — the doc is being accepted (disposed server-side). Whether we
      // advance to a sibling or land on a terminal, the exit-release must NOT
      // fire for this doc. On a genuine accept FAILURE the catch re-arms it so
      // the operator who then navigates away still releases.
      releaseSuppressRef.current = true;
      acceptDocument();
      incrementSessionStat("documentsKeyed");
      evict(documentId);

      // R-189 — trust the server's authoritative next-action verdict. The old
      // code POSTed accept then speculatively GET /batches/:id to hunt for a
      // "ready" sibling — a stale-cache race that landed the operator on a
      // phantom doc (blank canvas) at the end of a batch with no completion
      // feedback. The server now returns nextDocumentId/batchComplete inline.
      const landOnTerminal = (fallback: "queue" | "feed-exhausted") => {
        const terminalPath = fallback === "feed-exhausted" ? "/queues/feed-exhausted" : "/queues";
        // F-043 — only re-feed in continuous-feed mode. With no feed lock the
        // operator opened a single doc/batch; accepting the last ready doc must
        // close the batch and return to /queues, not pull a phantom next item.
        if (!shouldRefeedAfterAccept(fallback)) {
          navigate(terminalPath);
          return;
        }
        api
          .post<GetNextResponse>("/queues/next", { connectionId: crypto.randomUUID() })
          .then((result) => {
            const outcome = resolveRefeedOutcome(result);
            if (outcome.kind === "navigate") {
              navigate(outcome.to);
              return;
            }
            // WU-2.4 T8 — an eviction refused this re-claim. Treat exactly
            // like the supervisor feedBlocked case above: stop the feed and
            // show QueueBlockedModal instead of silently landing on the
            // terminal (which would read as "queue empty", not "evicted").
            // WU-2.4 /simplify — outcome.cause is always "evicted" here.
            if (outcome.kind === "blocked") {
              setQueueBlockedReason(outcome.reason);
              setQueueBlockedCause(outcome.cause);
              void stopFeed();
              return;
            }
            navigate(terminalPath);
          })
          .catch(() => navigate(terminalPath));
      };

      // R-239 P4 — report the smart-skip visit set so the server can record,
      // per profile-learning field, whether it was presented or skipped. Use
      // the snapshot taken before acceptDocument() flipped field state, so the
      // reported set reflects what the operator actually keyed/skipped.
      const presentedFields = computeAcceptPresentedFields(
        fieldsBeforeAccept,
        jtVerifyThreshold,
        useVerificationStore.getState().stepReverifyPolicy,
      );

      // F-063 — flush pending field persists before the accept POST (the
      // Ctrl+Enter-inside-the-debounce-window race that exported an empty
      // value). A flush failure falls into the catch below → rollback + Retry.
      return postAfterKeyedFlush<AcceptResponse>(`/documents/${documentId}/actions`, {
        action: "accept",
        presentedFields,
      })
        .then((resp) => {
          // S-5 Task 7 — a resolved accept clears any stale boundary spinner
          // from a prior checks_pending retry loop.
          setWebhookBoundary(null);
          const outcome = resolveAcceptOutcome(resp, {
            feedLock: Boolean(feedLock),
            // E-050 — shared preferred-label precedence (customDisplayLabel
            // ?? displayId ?? name); "current" only for a legacy batch with
            // no identity at all.
            batchLabel: batchLabel || "current",
            batchDocCount: totalDocsForAccept,
          });

          if (outcome.kind === "navigate") {
            navigate(outcome.to);
            return;
          }
          // R-162 §10.1 — the feed lane was blocked by a supervisor. Pop the
          // QueueBlockedModal with the reason and stop the feed lock so
          // /queues/next can't keep re-feeding the parked lane. The operator's
          // only forward action is "Pick another feed" (→ /queues). We do NOT
          // try /queues/next here — the lane is parked.
          if (outcome.kind === "blocked") {
            setQueueBlockedReason(outcome.reason);
            setQueueBlockedCause("blocked");
            void stopFeed();
            return;
          }
          if (outcome.kind === "complete") {
            toast.success(outcome.toast);
          }
          landOnTerminal(outcome.fallback);
        })
        .catch((err) => {
          // R-89 — a genuine accept rejection (validation 4xx, conflict,
          // network) was previously swallowed (no .catch). Roll back the
          // optimistic state, surface an actionable message, and stay on the
          // document so the operator can retry — never navigate away.
          useVerificationStore.setState({ fields: fieldsBeforeAccept });
          // F-054 — accept failed; the doc is still owned + on-screen. Re-arm
          // the exit-release so a later route-away releases it normally.
          releaseSuppressRef.current = false;

          // S-5 Task 7 — "wait and retry" (design §4/§5), not a genuine
          // stop: show the boundary spinner instead of the error toast.
          // Ctrl+Enter semantics are unchanged — runAcceptGuarded already
          // re-arms on this settle path, so a re-press retries exactly like
          // the toast's "Retry" button would; useWebhookCheckUpdates'
          // onResolved (wired below) also auto-retries once the last
          // outstanding check resolves.
          const classified = classifyAcceptError(err);
          if (classified.kind === "checks_pending") {
            setWebhookBoundary({ pendingChecks: classified.pendingChecks });
            return;
          }
          setWebhookBoundary(null);
          toast.error(acceptErrorMessage(err), {
            label: "Retry",
            onClick: () => void handleAccept(),
          });
        });
    });
  }, [
    documentId,
    acceptDocument,
    incrementSessionStat,
    evict,
    navigate,
    feedLock,
    stopFeed,
    batchLabel,
    totalDocsForAccept,
    jtVerifyThreshold,
  ]);

  // S-5 Task 7 — keep the ref current for handleOverrideDialogConfirm /
  // useWebhookCheckUpdates' onResolved below (both declared before/around
  // handleAccept and need to call the LATEST closure, not a stale one).
  useEffect(() => {
    handleAcceptRef.current = handleAccept;
  }, [handleAccept]);

  // S-5 Task 7 — webhook-check WS subscription (design §5). onResolved is
  // the boundary-spinner auto-retry: a resolving check while
  // webhookBoundary is set re-attempts accept (if other checks are still
  // pending the server 409s again with an updated pendingChecks — the
  // spinner just updates its count). onUnresolved routes to the override
  // dialog (soft/escalate — reuses the SAME OverrideDialog machinery via
  // the `source: "webhook"` discriminator above) or the hold banner.
  useWebhookCheckUpdates({
    currentDocumentId: documentId,
    onResolved: () => {
      setWebhookBoundary((prev) => {
        if (!prev) return prev;
        handleAcceptRef.current();
        return null;
      });
    },
    onUnresolved: (msg) => {
      // S-5 Task 7 review round 2, item 3 — an 'unresolved' event is a
      // TERMINAL outcome for that check, same as a resolving one: if the
      // boundary spinner was up (e.g. one check resolved to 'passed' while
      // this one flipped to 'unresolved'), it must clear here too, or it
      // spins forever on a check that has already left 'pending'.
      setWebhookBoundary(null);

      const action = resolveWebhookUnresolvedAction(msg.onUnresolved);
      // S-5 Task 7 review round 2, item 1 — resolve the REAL FieldState so
      // the override dialog (and, on confirm, persistWebhookOverride) has a
      // genuine DocumentField id to write against — `msg.fieldRef` is now
      // on the wire (T6/T7 alignment, commit 10c0356e).
      const field = msg.fieldRef
        ? useVerificationStore.getState().fields.find((f) => f.name === msg.fieldRef)
        : undefined;
      if (action === "hold") {
        setWebhookHoldBanner({ ruleName: msg.ruleName ?? "External check" });
        return;
      }
      setOverrideDialog({
        fieldId: field?.id ?? "",
        source: "webhook",
        mode: action === "escalate" ? "escalate" : "override",
        requireReason: true,
        ruleName: msg.ruleName ?? "Webhook check",
        fieldLabel: field?.label ?? msg.ruleName ?? "Webhook check",
        currentValue: field?.value ?? "",
        ruleMessage: msg.message ?? undefined,
      });
    },
  });

  // F-095 — the ONE validated accept path (see runValidatedSubmit): wired as
  // KeyboardManager's onAccept AND the formReadyToSubmit effect body below.
  const validateThenAccept = useValidatedAccept(handleAccept);
  // F-023 — the submit gate parks un-acknowledged standing warnings here; the
  // ConfirmModal below lists them and resumes the validated accept on confirm.
  const pendingWarnAck = useVerificationStore((s) => s.pendingWarnAck);

  // Form ready: run full revalidation, then submit or jump to failures.
  //
  // R-127 step 7 (Phase 1.5 WU-4) — "On exit from the last low-confidence
  // field, run all field rules + all form-scope rules together for the
  // whole form". `advanceField` flips `formReadyToSubmit=true` when no
  // more fields need edit (which IS "the cursor exited the last low-conf
  // field" under the smart-skip iteration semantics). The whole-form rule
  // pass lives in `runValidatedSubmit` (F-095 extracted it from this effect
  // so the explicit accept gestures share it): Tier 1 across every field +
  // content-native server validation, with cursor jump to the first failure
  // or document advance on full pass.
  useEffect(() => {
    if (!formReadyToSubmit) return;
    useVerificationStore.setState({ formReadyToSubmit: false });
    validateThenAccept();
  }, [formReadyToSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReject = useCallback(async () => {
    if (!documentId) return;
    const reason = prompt("Rejection reason:");
    if (reason === null) return;
    try {
      // F-063 — reject snapshots DB keyedValues too; flush pending persists first.
      await postAfterKeyedFlush(`/documents/${documentId}/actions`, { action: "reject", reason });
      // F-054 — reject already dispositioned the doc server-side; suppress the
      // exit-release on the navigate that follows.
      releaseSuppressRef.current = true;
      navigate("/queues");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to reject document";
      toast.error(msg, { label: "Retry", onClick: () => void handleReject() });
    }
  }, [documentId, navigate]);

  const handleHold = useCallback(async () => {
    if (!documentId) return;
    try {
      // F-063 — a hold inside the debounce window parked the doc with the
      // last-typed value unpersisted (silently gone on resume). Flush first.
      await postAfterKeyedFlush(`/documents/${documentId}/actions`, { action: "hold" });
      // F-054 — hold dispositioned the doc server-side; suppress exit-release.
      releaseSuppressRef.current = true;
      navigate("/queues");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to hold document";
      toast.error(msg, { label: "Retry", onClick: () => void handleHold() });
    }
  }, [documentId, navigate]);

  const handleFlagMisidentified = useCallback(
    async (note: string) => {
      if (!documentId) return;
      if (flaggingRef.current) return;
      flaggingRef.current = true;
      setMisidentOpen(false);
      try {
        // F-063 — escalate disposes the doc server-side; flush pending persists first.
        const result = await postAfterKeyedFlush<{ action?: string }>(`/documents/${documentId}/escalate`, {
          reason: "Wrong document type",
          category: "misidentified_type",
          note: note.length > 0 ? note : undefined,
        });
        // R-248 — when the job type has no escalation step the escalate is held,
        // not queued; tell the keyer the truth instead of a false success.
        const outcome = escalateFlagOutcome(result?.action);
        if (outcome.ok) toast.info(outcome.message);
        else toast.error(outcome.message);
        // F-054 — escalate/flag dispositioned the doc server-side; suppress
        // the exit-release on the navigate that follows.
        releaseSuppressRef.current = true;
        navigate("/queues");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to flag document";
        toast.error(msg, { label: "Retry", onClick: () => setMisidentOpen(true) });
      } finally {
        flaggingRef.current = false;
      }
    },
    [documentId, navigate, setMisidentOpen],
  );

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <ContentValidateContext.Provider value={contentValidateRules}>
      <LookupSnapshotContext.Provider value={contentLookupData}>
      <JobConstantsContext.Provider value={contentConstants}>
      <div className="verification-station">
        {/* F-095 — onAccept must stay the validated path, never raw handleAccept. */}
        <KeyboardManager
          onAccept={validateThenAccept}
          onReject={handleReject}
          onHold={handleHold}
          onToggleShortcutHelp={() => setShowShortcuts((v) => !v)}
          onOverride={handleOverride}
        />

        <KeyboardShortcutModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />

        <MisidentifiedTypeDialog
          open={misidentOpen}
          onConfirm={handleFlagMisidentified}
          onCancel={() => setMisidentOpen(false)}
        />

        {/* F-023 — standing-warning acknowledgment. The submit gate paused the
            accept because un-acknowledged warn fields exist; confirming records
            the acks and resumes the SAME validated accept path (no bypass). */}
        <ConfirmModal
          open={pendingWarnAck !== null}
          title={`Accept with ${pendingWarnAck?.fields.length ?? 0} standing warning${(pendingWarnAck?.fields.length ?? 0) === 1 ? "" : "s"}?`}
          description={(pendingWarnAck?.fields ?? [])
            .map((f) => `${f.label}: ${f.message ?? "check this value"}`)
            .join("\n")}
          confirmLabel="Accept anyway"
          cancelLabel="Review fields"
          onConfirm={() => {
            useVerificationStore.getState().acknowledgeWarnings((pendingWarnAck?.fields ?? []).map((f) => f.id));
            useVerificationStore.getState().setPendingWarnAck(null);
            validateThenAccept();
          }}
          onCancel={() => useVerificationStore.getState().setPendingWarnAck(null)}
        />

        {/* R-162 — QC-step cross-batch confirm. Renders when the post-Accept
            auto-advance has navigated to a doc in a different batch and the
            operator is in a qa/supervisor step. */}
        <CrossBatchConfirmModal
          open={transition.qcModalOpen}
          fromBatch={transition.qcFromBatch}
          toBatch={transition.qcToBatch}
          onContinue={transition.acceptQc}
          onStop={transition.cancelQc}
        />

        {/* R-162 §10.1 — a supervisor parked the operator's feed lane while
            they keyed this doc (cause="blocked"), OR (WU-2.4) an eviction
            refused the continuous-feed re-claim (cause="evicted").
            Pick-another-feed clears state and returns to the queue
            dashboard. */}
        {queueBlockedReason !== null && (
          <QueueBlockedModal
            reason={queueBlockedReason}
            cause={queueBlockedCause}
            onPickAnotherFeed={() => {
              setQueueBlockedReason(null);
              setQueueBlockedCause("blocked");
              navigate("/queues");
            }}
          />
        )}

        {/* S-5 Task 7 — accept-boundary "wait and retry" (design §4/§5).
            Non-blocking (no modal — Ctrl+Enter re-press still works, same
            keyboard-first discipline as every other accept-path state): a
            checks_pending 409 shows this instead of the error toast, and it
            clears itself the moment a resolving webhook_check_update lands
            (useWebhookCheckUpdates' onResolved auto-retries accept above). */}
        {webhookBoundary !== null && (
          <div
            className="webhook-boundary-spinner"
            role="status"
            data-testid="webhook-boundary-spinner"
          >
            <span className="webhook-check-chip__spinner" aria-hidden="true" />
            {webhookBoundary.pendingChecks} check{webhookBoundary.pendingChecks === 1 ? "" : "s"} resolving…
            {/* S-5 Task 7 review round 2, item 2 — the operator's escape
                hatch onto the built-but-previously-unreachable refire
                route. A real <button> (keyboard-reachable, default tabIndex
                0), never auto-focused. */}
            <button
              type="button"
              className="webhook-boundary-retry-btn"
              data-testid="webhook-boundary-retry"
              onClick={handleRetryAllPending}
              title="Force a fresh dial on every check that's still resolving"
            >
              Retry check
            </button>
          </div>
        )}

        {/* S-5 Task 7 — onUnresolved:'hold' (design §5 "batch banner 'held —
            unresolved external checks'"). No existing "batch held" component
            was found that fit without a forced fit or an out-of-scope file
            edit — see task-7-report.md — so this is a small new inline
            banner using the design spec's own copy. */}
        {webhookHoldBanner !== null && (
          <div
            className="webhook-hold-banner"
            role="status"
            data-testid="webhook-hold-banner"
          >
            <strong>Held</strong> — unresolved external checks
            {webhookHoldBanner.ruleName ? ` (${webhookHoldBanner.ruleName})` : ""}.
            This document can&rsquo;t be accepted until it resolves.
          </div>
        )}

        <div className="vs-header">
          {/* E-050 — lead with the unique batch identity (customDisplayLabel
              ?? displayId ?? name); the free-text name keeps operator value
              as the hover tooltip when it differs. */}
          <span
            className="vs-batch"
            title={batchName && batchName !== batchLabel ? batchName : undefined}
          >
            {batchLabel}
          </span>
          {/* F-019 — always-visible stage badge. The key→verify handoff was
              previously invisible (stage only surfaced in transient toasts),
              so operators read the verify pass as the batch "looping". */}
          {currentStep && (
            <span className="vs-stage-badge" data-testid="vs-stage-badge">
              {formatStep(currentStep)}
            </span>
          )}
          {/* R-167 WU-7 — preferredDisplayLabel(doc): customDisplayLabel wins
              over displayId. Hidden when both are null (legacy doc) — the
              filename below is still rendered as the muted sub-line, so
              operators always have *some* identifier. */}
          {(docCustomDisplayLabel || docDisplayId) && (
            <span
              className="vs-display-id"
              data-testid="vs-display-id"
            >
              {docCustomDisplayLabel ?? docDisplayId}
            </span>
          )}
          <span className="vs-filename">{filename}</span>
          {/* E-004 — operator session timer (standard VS header). Terminal mode
              has its own timer in the stats bar; both reuse formatDuration. */}
          <SessionTimer />
          <span style={{ flex: 1 }} />
          <FieldRuleReadout />
          {documentId && currentStep && !isLoading && (
            <VerificationKeystrokeRecorder
              documentId={documentId}
              step={currentStep}
              active={!terminalMode}
              operatorId={user?.userId ?? null}
              // R-235 B2 — when a supervisor bumps this doc, onBump sets
              // bumpedRef.current = true before navigate(); the recorder reads
              // it at unmount and drops the in-progress keystroke snapshot.
              skipFinalFlushRef={bumpedRef}
            />
          )}
          <VerificationCheckInButton currentDocBatchId={batchIdForTransition} />
          <button
            className="vs-detach-btn"
            onClick={() => setShowAuditTrail((v) => !v)}
            title="Toggle audit trail"
            data-testid="audit-trail-toggle"
          >
            {showAuditTrail ? "Hide Audit" : "Audit Trail"}
          </button>
          {/* R-134 / Phase 1.5 WU-4 — Boxes Legend moved from floating overlay
              to a header-docked popover. Matches Audit Trail / Detach Panel.
              R-139 — Boxes overlay toggle follows the same docking pattern. */}
          <BboxOverlayHeaderButton />
          <BboxLegendHeaderButton />
          {/* R-148 / R-164 — Toggle between Fit Width ↔ Fit Page. Label shows
              the fit that will be applied on the NEXT click, so operators see
              what they're about to get. Toggling clears any saved manual zoom
              so the new fit actually takes effect (see store.toggleFitMode). */}
          <FitModeButton />

          <button className="vs-detach-btn" onClick={isDetached ? reattach : detach} title={isDetached ? "Reattach field panel" : "Pop out field panel to separate window"}>
            {isDetached ? "⊞ Reattach" : "⊟ Detach Panel"}
          </button>
        </div>

        {/* WU-2.2 — standing instructions for this doc's lane. Renders null
            when the lane has none; never intercepts focus (F-077). Mounted
            outside the terminalMode branch below, so it appears in BOTH
            standard and terminal VS modes — desired, since the header region
            it docks under is shared by both. */}
        {currentStep && (
          <QueueInstructionStrip stepType={currentStep} jobTypeId={jobTypeId} />
        )}

        {/* Task 12 (WU-2.3) — doc-anchored supervisor notes. Same placement
            rule as QueueInstructionStrip above: outside the terminalMode
            branch so it renders in both standard and terminal VS modes;
            null-when-empty; never touches focus (F-077). */}
        {documentId && <DirectedNoteCard documentId={documentId} />}

        {/* R-134 — BboxLegend is anchored to the station root (next to the
            header it docks off of), not to the canvas. Keeps it from being
            clipped by .vs-document overflow and lets it position relative
            to the header button. */}
        <BboxLegend />

        {terminalMode ? (
          <div className="vs-terminal">
            <TerminalModeView />
          </div>
        ) : (
          <div className="vs-panels">
            <BatchTreeDrawer />
            <div className="vs-document">
              <DocumentCanvas />
            </div>
            {!isDetached && (
              <div className="vs-right-panel">
                <SnippetPanel />
                <div className="vs-fields">
                  <FieldPanel />
                  <LineItemGridsMount />
                </div>
              </div>
            )}
            {showAuditTrail && documentId && (
              <AuditTrailPanel documentId={documentId} onClose={() => setShowAuditTrail(false)} />
            )}
          </div>
        )}

        <StatusBar
          onReject={handleReject}
          onHold={handleHold}
          onShowShortcuts={() => setShowShortcuts((v) => !v)}
          onFlagMisidentified={canFlagMisidentified ? () => setMisidentOpen(true) : undefined}
        />
        <RevalidationOverlay />

        {/* R-239 B4 — Ctrl+O override / escalate dialog */}
        <OverrideDialog
          isOpen={overrideDialog !== null}
          mode={overrideDialog?.mode ?? "override"}
          ruleName={overrideDialog?.ruleName ?? ""}
          fieldLabel={overrideDialog?.fieldLabel ?? ""}
          currentValue={overrideDialog?.currentValue ?? ""}
          fallbackValue={overrideDialog?.fallbackValue}
          escalationQueue={overrideDialog?.escalationQueue}
          requireReason={overrideDialog?.requireReason ?? false}
          onConfirm={handleOverrideDialogConfirm}
          onCancel={() => setOverrideDialog(null)}
        />

        {/* R-239 — the VS renders OUTSIDE AppShell (App.tsx), so AppShell's
            Toaster never covers this route. Mount one here so transient
            feedback (override success / 403, batch-complete) actually shows.
            Also closes R-226 (batch-complete toast never rendered). */}
        <Toaster />

      </div>
      </JobConstantsContext.Provider>
      </LookupSnapshotContext.Provider>
    </ContentValidateContext.Provider>
  );
}

/**
 * Test-only re-export (legacy seam). The mapping itself now lives in
 * ../lib/map-api-to-payload (F-097 — the prefetch layer maps at cache-store
 * time); this alias is kept so existing mapping tests keep importing from
 * the component module.
 */
export { mapApiToPayload as mapApiToPayloadForTest };

/**
 * R-164 — Header-docked Fit Width ↔ Fit Page toggle.
 *
 * Label shows the fit that will be applied on the NEXT click — i.e. when
 * fitMode is "width" we're currently width-fit, so the next click applies
 * Fit Page (and vice versa). This matches what the operator expects: the
 * button reads "Fit Page" when clicking it will fit-to-page.
 *
 * Implementation note: kept inline in this module instead of a separate
 * file because it's a one-button wrapper around `toggleFitMode`. The
 * data-testid mirrors the original R-148 button so existing assertions
 * still find it.
 */
export function FitModeButton() {
  const fitMode = useVerificationStore((s) => s.fitMode);
  const toggleFitMode = useVerificationStore((s) => s.toggleFitMode);
  // R-198 — persist the chosen fit mode so it survives doc/batch transitions.
  // The store-only R-164 wiring reset on VS unmount during navigation.
  const persistFitMode = useViewerFitModePersist();
  // Label = the fit that will be applied on the NEXT click. When fitMode is
  // "width" the image is currently width-fit, so the next click applies Fit
  // Page — and vice versa.
  const next = fitMode === "width"
    ? { label: "⊟ Fit Page", aria: "Fit Page", title: "Fit entire page in viewer" }
    : { label: "⇔ Fit Width", aria: "Fit Width", title: "Fit document width to viewer" };
  const handleClick = () => {
    // Compute the mode we're switching TO (toggleFitMode flips it in the
    // store) and persist that. Reading fitMode here is the pre-toggle value.
    const nextMode = fitMode === "width" ? "page" : "width";
    toggleFitMode();
    persistFitMode(nextMode);
  };
  return (
    <button
      type="button"
      className="vs-detach-btn"
      onClick={handleClick}
      title={next.title}
      data-testid="fit-to-width-btn"
      aria-label={next.aria}
    >
      {next.label}
    </button>
  );
}
